import { useEffect, useLayoutEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Container, Stage } from '@pixi/react';

import { configuratedStage } from '../../../jackpotModule/helpers/configuratedWheelStage';
import { stageOptions } from '../../../frameModule/constants/gameConstants';
import { BoostedJackpotWheelWrapper } from './BoostedJackpotWheelWrapper';
import { BoostedJackpotWinPopup } from './boostedJackpotWinPopup';
import { Providers } from '../../../../../view/containers';
import { getGameImages } from '../../../../helpers';
import { useStore } from '../../../../../hooks';

export const BoostedJackpotPopup: React.FC = observer(() => {
  const { boostedJackpotStore } = useStore();
  const {
    wheelValues,
    setIsBoostedPopupOpen,
    setIsWheel,
    isBoostedPopupOpen,
    hasBoostedJackpot,
    isWheelZoomOut,
    boostedJackpotResourcesLoaded,
  } = boostedJackpotStore;
  const boostedRefApp = useRef<any>(null);
  const amountSlotRef = useRef<any>(null);
  const congratSlotRef = useRef<any>(null);
  const bgSpineRef = useRef<any>(null);
  const buttonSlotRef = useRef<any>(null);
  const wheelWrapperContainerRef = useRef<any>(null);
  const {
    cursor,
    wheelBtn,
    wheelBorder,
  } = getGameImages();
  const centeredWheelConfig = {
    x: 0,
    y: 0,
  };

  useLayoutEffect(() => {
    if (!boostedJackpotResourcesLoaded) return;
    if (!boostedRefApp.current) return;
    configuratedStage(boostedRefApp);
  }, [boostedJackpotResourcesLoaded]);

  // Keep the canvas/stage and the centered wrapper container responsive to window resize.
  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;

      const app = boostedRefApp.current?.app;
      if (app?.renderer?.resize) {
        app.renderer.resize(w, h);
      }

      const wrapper = wheelWrapperContainerRef.current;
      if (wrapper?.position) {
        wrapper.position.set(w / 2, h / 2);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [boostedJackpotResourcesLoaded]);

  useEffect(() => {
    if (!hasBoostedJackpot) return;

    setIsWheel(false);
    setIsBoostedPopupOpen(true);

    const wheelOpenTimeoutId = window.setTimeout(() => {
      setIsWheel(true);
    }, 600);

    return () => {
      window.clearTimeout(wheelOpenTimeoutId);
      setIsWheel(false);
      setIsBoostedPopupOpen(false);
    };
  }, [hasBoostedJackpot, setIsBoostedPopupOpen, setIsWheel]);

  const shouldRenderCanvas = isBoostedPopupOpen || isWheelZoomOut;
  if (!boostedJackpotResourcesLoaded || !wheelValues?.length || !shouldRenderCanvas) return null;

  return (
    <div className='wheel-canvas'
    >
      <Stage
        id='boostedJackpotWheel'
        width={window.innerWidth}
        height={window.innerHeight}
        ref={boostedRefApp}
        options={stageOptions}
      >
        <Providers>
          <Container {...({ name: 'BoostedJackpotContainer' } as any)}>
            <Container
              ref={wheelWrapperContainerRef}
              x={window.innerWidth / 2}
              y={window.innerHeight / 2}
              {...({ name: 'BoostedJackpotWheelWrapperContainer' } as any)}
            >
              <BoostedJackpotWheelWrapper
                cursor={cursor}
                wheelBtn={wheelBtn}
                wheelBorder={wheelBorder}
                wheelValues={wheelValues}
                sectors={wheelValues.length}
                containerConfigBoostedJackpotWheel={centeredWheelConfig}
                amountSlotRef={amountSlotRef}
                congratSlotRef={congratSlotRef}
                bgSpineRef={bgSpineRef}
                buttonSlotRef={buttonSlotRef}
              />
            </Container>
            <BoostedJackpotWinPopup
              amountSlotRef={amountSlotRef}
              congratSlotRef={congratSlotRef}
              bgSpineRef={bgSpineRef}
              buttonSlotRef={buttonSlotRef}
            />
          </Container>
        </Providers>
      </Stage>
    </div>
  );
});
